import type { SubmitRevertContext } from '../word/trackChanges/RedlineInserter';

function isTrackedChangeFingerprint(value: unknown): value is { dateIso: string; type: string } {
  if (!value || typeof value !== 'object') return false;
  const fp = value as Record<string, unknown>;
  return typeof fp.dateIso === 'string' && typeof fp.type === 'string';
}

/** Normalize persisted submission_status.response_revert_context from the API. */
export function parseSubmitRevertContext(value: unknown): SubmitRevertContext | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as Record<string, unknown>;
  const applyStartedAtMs = Number(raw.applyStartedAtMs);
  const applyFinishedAtMs = Number(raw.applyFinishedAtMs);
  const paragraphIndexRaw = Number(raw.paragraphIndex);
  if (!Number.isFinite(applyStartedAtMs) || !Number.isFinite(applyFinishedAtMs)) {
    return undefined;
  }
  const fingerprints = Array.isArray(raw.addedFingerprints)
    ? raw.addedFingerprints.filter(isTrackedChangeFingerprint)
    : [];
  const paragraphIndex =
    Number.isFinite(paragraphIndexRaw) && paragraphIndexRaw >= 0 ? paragraphIndexRaw : -1;
  // Preserve Win32-undo fields if present — needed for TC-off + insertOoxml/insertText restore.
  const originalParagraphText =
    typeof raw.originalParagraphText === 'string' && raw.originalParagraphText.length > 0
      ? raw.originalParagraphText
      : undefined;
  const originalParagraphOoxml =
    typeof raw.originalParagraphOoxml === 'string' && raw.originalParagraphOoxml.length > 0
      ? raw.originalParagraphOoxml
      : undefined;
  const originalParagraphOoxmlPackage =
    typeof raw.originalParagraphOoxmlPackage === 'string' &&
    raw.originalParagraphOoxmlPackage.length > 0
      ? raw.originalParagraphOoxmlPackage
      : undefined;
  const preferOriginalRestore = raw.preferOriginalRestore === true;
  const inTable = raw.inTable === true;
  return {
    applyStartedAtMs,
    applyFinishedAtMs,
    addedFingerprints: fingerprints,
    paragraphIndex,
    ...(originalParagraphText != null ? { originalParagraphText } : {}),
    ...(originalParagraphOoxml != null ? { originalParagraphOoxml } : {}),
    ...(originalParagraphOoxmlPackage != null ? { originalParagraphOoxmlPackage } : {}),
    ...(preferOriginalRestore ? { preferOriginalRestore } : {}),
    ...(inTable ? { inTable } : {}),
  };
}

/** Prefer the context that is more likely to undo the latest submit apply. */
export function isRicherSubmitRevertContext(
  candidate: SubmitRevertContext | undefined,
  existing: SubmitRevertContext | undefined,
): boolean {
  if (!candidate) return false;
  if (!existing) return true;
  const candidateFp = candidate.addedFingerprints.length;
  const existingFp = existing.addedFingerprints.length;
  if (candidateFp !== existingFp) return candidateFp > existingFp;
  // Never downgrade from a context that has originalParagraphText (needed for Win32 undo) to
  // one that does not — even when fingerprint counts and timestamps are equal. This prevents
  // syncSubmitRevertContextFromClauses from overwriting a freshly-captured local context
  // (which carries originalParagraphText) with the server-returned context (which does not).
  if (candidate.originalParagraphText == null && existing.originalParagraphText != null) {
    return false;
  }
  // Same rule for the full original package — it is the preferred Win32 undo input. Never
  // downgrade from a context that has it to one that does not.
  if (
    candidate.originalParagraphOoxmlPackage == null &&
    existing.originalParagraphOoxmlPackage != null
  ) {
    return false;
  }
  return candidate.applyFinishedAtMs >= existing.applyFinishedAtMs;
}

export function submitRevertContextFromSubmissionStatus(
  submissionStatus?: {
    response_submitted?: boolean;
    response_revert_context?: unknown;
  } | null,
): SubmitRevertContext | undefined {
  if (!submissionStatus?.response_submitted) return undefined;
  return parseSubmitRevertContext(submissionStatus.response_revert_context);
}
